<template>
    <DsModal
        :is-open="isOpen"
        show-close
        class="add-product-modal"
        data-qa="add-product-modal"
        size="med"
        :title="$t('funnel.edit.createOrder.createProduct.modal.title')"
        @close="closeModal"
    >
        <template #header>
            <DsFilledButton
                data-qa="add-product-button"
                @click="createProduct"
            >
                {{ $t('funnel.edit.createOrder.createProduct.submit.button.label') }}
            </DsFilledButton>
        </template>
        <div class="add-product-modal-content">
            <DsInputField
                v-model="product.name"
                data-qa="add-product-name"
                type="text"
                :label="$t('funnel.edit.createOrder.createProduct.field.name.label')"
                :placeholder="$t('funnel.edit.createOrder.createProduct.field.name.label')"
                :submitted="invalidProductName"
                :invalid="invalidProductName"
                required
            >
                <template #error>
                    {{ $t('funnel.edit.createOrder.createProduct.field.name.error') }}
                </template>
            </DsInputField>
            <DsTextAreaField
                v-model="product.shortDesc"
                class="product-short-desc"
                data-qa="add-product-short"
                type="text"
                :maxlength="100"
                :label="$t('funnel.edit.createOrder.createProduct.field.shortDesc.label')"
            >
                <template #help>
                    <div class="characters-left">
                        {{ charactersLeft }}
                    </div>
                </template>
            </DsTextAreaField>
            <DsMultiselect
                v-model="product.productType"
                data-qa="add-product-type"
                bind-value-only
                :placeholder="$t('funnel.edit.createOrder.createProduct.type.product.title')"
                :options="productTypesOptions"
                :label="$t('funnel.edit.createOrder.createProduct.type.product.title')"
            />
            <DsInputField
                v-model="product.price"
                data-qa="add-product-price"
                type="number"
                :label="$t('funnel.edit.createOrder.createProduct.field.price.label')"
            >
                <template #leading>
                    <DsIcon name="dollar" />
                </template>
            </DsInputField>
            <DsMultiselect
                v-model="product.addAs"
                data-qa="add-product-add-as"
                bind-value-only
                :placeholder="$t('funnel.edit.createOrder.createProduct.field.addAs.placeholder')"
                :options="addAsTypesOptions"
                :label="$t('funnel.edit.createOrder.createProduct.field.addAs.placeholder')"
            />
            <div v-if="isSubscription" class="billing-cycle-container">
                <div>{{ $t('funnel.edit.createOrder.createProduct.recurring.frequency.billing.every') }}</div>
                <DsInputField
                    v-model="product.billEvery"
                    data-qa="add-product-bill-every"
                    type="number"
                    :step="1"
                    :min="0"
                    :max="9999"
                />
                <DsMultiselect
                    v-model="product.planCycle"
                    data-qa="add-product-plan-cycle"
                    :placeholder="$t('funnel.edit.createOrder.createProduct.field.billingInterval.placeholder')"
                    :options="billingCycleIntervals"
                    required
                    bind-value-only
                    :force-invalid="invalidPlanCycle"
                >
                    <template #help>
                        <span
                            v-if="invalidPlanCycle"
                            data-qa="invalid-plan-cycle"
                        >
                            {{ $t('funnel.edit.createOrder.createProduct.field.name.error') }}
                        </span>
                    </template>
                </DsMultiselect>
            </div>
            <DsInputField
                v-if="isSubscription"
                v-model="product.amountOfCycles"
                data-qa="add-amount-cycles"
                type="number"
                :step="1"
                :min="0"
                :max="9999"
                :label="$t('funnel.edit.createOrder.createProduct.recurring.cycles')"
            />
            <DsInputField
                v-if="isSubscription"
                v-model="product.planPrice"
                data-qa="add-product-plan-price"
                type="number"
                :step="1"
                :min="0"
                :label="$t('funnel.edit.createOrder.createProduct.recurring.price')"
            >
                <template #leading>
                    <DsIcon name="dollar" />
                </template>
            </DsInputField>
        </div>
    </DsModal>
</template>

<script>

const ADD_AS_TYPES = { PRODUCT: 'Product', SUBSCRIPTION: 'Subscription' };
const PRODUCT_TYPES = { PRODUCT: '0', DIGITAL: '1' };
const RECURRING_FREQUENCIES = {
    DAY: '6',
    WEEK: '3',
    MONTH: '2',
    YEAR: '1',
};

export default {
    ADD_AS_TYPES,
    RECURRING_FREQUENCIES,
    PRODUCT_TYPES,


    props: {
        isSpaApp: {
            type: Boolean,
            required: false,
            default: false,
        },
        isOpen: {
            type: Boolean,
            required: true,
        },
        suggestedName: {
            type: String,
            default: () => (undefined),
        },
    },

    emits: ['close-modal', 'create-product'],

    data() {
        return {
            product: {
                name: '',
                shortDesc: '',
                productType: PRODUCT_TYPES.PRODUCT,
                price: 0,
                addAs: ADD_AS_TYPES.PRODUCT,
                billEvery: 0,
                planPrice: 0,
                planCycle: null,
                amountOfCycles: 0,
            },
            invalidProductName: false,
            invalidPlanCycle: false,
        };
    },

    computed: {
        billingCycleIntervals() {
            return [
                { value: RECURRING_FREQUENCIES.DAY, label: this.$t('funnel.edit.createOrder.createProduct.recurring.frequency.Day') },
                { value: RECURRING_FREQUENCIES.WEEK, label: this.$t('funnel.edit.createOrder.createProduct.recurring.frequency.Week') },
                { value: RECURRING_FREQUENCIES.MONTH, label: this.$t('funnel.edit.createOrder.createProduct.recurring.frequency.Month') },
                { value: RECURRING_FREQUENCIES.YEAR, label: this.$t('funnel.edit.createOrder.createProduct.recurring.frequency.Year') },
            ];
        },
        addAsTypesOptions() {
            const options = [
                { value: ADD_AS_TYPES.PRODUCT, label: this.$t('funnel.edit.createOrder.createProduct.addAsType.product.label') },
                { value: ADD_AS_TYPES.SUBSCRIPTION, label: this.$t('funnel.edit.createOrder.createProduct.addAsType.subscription.label'), hidden: this.isSpaApp },
            ];

            return options.filter((option) => !option.hidden);
        },

        productTypesOptions() {
            return [
                { value: PRODUCT_TYPES.PRODUCT, label: this.$t('funnel.edit.createOrder.createProduct.type.product.label') },
                { value: PRODUCT_TYPES.DIGITAL, label: this.$t('funnel.edit.createOrder.createProduct.type.digital.label') },
            ];
        },

        isSubscription() {
            return this.product.addAs === ADD_AS_TYPES.SUBSCRIPTION;
        },

        isInvalidForm() {
            return this.invalidProductName || (this.isSubscription && this.invalidPlanCycle);
        },

        charactersLeft() {
            return `${this.product.shortDesc.length}/100`;
        },
    },

    mounted() {
        if (this.suggestedName) {
            this.product.name = this.suggestedName;
        }
    },

    methods: {
        closeModal() {
            this.$emit('close-modal');
        },
        createProduct() {
            this.validateFields();

            if (!this.isInvalidForm) {
                this.$emit('create-product', this.product);
            }
        },
        validateFields() {
            this.invalidProductName = false;
            this.invalidPlanCycle = false;
            this.setIsValidProductName();
            this.setIsValidPlanCycle();
        },
        setIsValidProductName() {
            this.invalidProductName = this.product.name === '';
        },
        setIsValidPlanCycle() {
            this.invalidPlanCycle = this.product.planCycle === null || this.product.planCycle === '';
        },
    },
};
</script>

<style lang="scss" scoped>
.billing-cycle-container {
    display: grid;
    align-items: baseline;
    grid-template-columns: auto 1fr 2fr;
    grid-gap: $spacing-400;
}

.characters-left {
    float: right;
}

.product-short-desc {
    height: fit-content;
    :deep(textarea) {
        height: px-to-rem(60);
        min-height: px-to-rem(60);
    }
}
</style>
